import { useCallback, useEffect, useRef, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import type { AuthResponse } from '@xingroute/schema'
import { api } from '../api/client.js'
import { useLocale } from '../locale/LocaleContext.js'
import { Button } from './ui/Button.js'

const MAX_PRECROP_BYTES = 5 * 1024 * 1024
const OUTPUT_SIZE = 512

async function getCroppedWebp(src: string, crop: Area): Promise<File> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = () => reject(new Error('image load failed'))
    el.src = src
  })
  const canvas = document.createElement('canvas')
  canvas.width = OUTPUT_SIZE
  canvas.height = OUTPUT_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no 2d context')
  ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE)
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/webp', 0.9),
  )
  if (!blob) throw new Error('canvas toBlob failed')
  return new File([blob], 'avatar.webp', { type: 'image/webp' })
}

export interface AvatarCropDialogProps {
  file: File
  token: string
  onUploaded: (res: AuthResponse) => void
  onClose: () => void
}

export function AvatarCropDialog({ file, token, onUploaded, onClose }: AvatarCropDialogProps) {
  const { t } = useLocale()
  const [zoom, setZoom] = useState(1)
  const [cropPos, setCropPos] = useState({ x: 0, y: 0 })
  const [pixels, setPixels] = useState<Area | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const srcRef = useRef<string>('')
  const [src, setSrc] = useState<string>('')

  useEffect(() => {
    if (file.size > MAX_PRECROP_BYTES) {
      setError(t('avatar.errors.tooLarge'))
      return
    }
    const url = URL.createObjectURL(file)
    srcRef.current = url
    setSrc(url)
    return () => URL.revokeObjectURL(url)
  }, [file, t])

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => setPixels(areaPixels), [])

  const tooLarge = file.size > MAX_PRECROP_BYTES

  async function confirm() {
    if (!pixels || busy || tooLarge) return
    setBusy(true)
    setError(null)
    try {
      const cropped = await getCroppedWebp(srcRef.current, pixels)
      const res = await api.uploadAvatar(cropped, token)
      onUploaded(res)
    } catch {
      setError(t('avatar.errors.failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--xr-overlay-bg)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('avatar.crop.title')}
      onClick={onClose}
      data-testid="avatar-crop-dialog"
    >
      <div
        className="w-full max-w-md space-y-4 rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-5 shadow-[var(--xr-shadow-md)]"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[var(--xr-text-primary)]">
          {t('avatar.crop.title')}
        </h2>
        {!tooLarge && (
          <div className="relative h-64 w-full overflow-hidden rounded-[var(--xr-radius-md)] bg-black/40">
            <Cropper
              image={src}
              crop={cropPos}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCropPos}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        )}
        {!tooLarge && (
          <label className="flex items-center gap-3 text-sm text-[var(--xr-text-secondary)]">
            {t('avatar.crop.zoom')}
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
              className="flex-1"
              aria-label={t('avatar.crop.zoom')}
            />
          </label>
        )}
        {error && (
          <p className="text-sm text-[var(--xr-danger-fg)]" data-testid="avatar-crop-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('avatar.crop.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={() => void confirm()}
            disabled={busy || tooLarge}
            data-testid="avatar-crop-confirm"
          >
            {busy ? '…' : t('avatar.crop.confirm')}
          </Button>
        </div>
      </div>
    </div>
  )
}
